import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { cn } from '@ownyou/ui-design-system';
import { Card, Badge, Button } from '@ownyou/ui-components';
import { useMission } from '../hooks/useMission';
import { useUpdateMission } from '../hooks/useMissions';
import { recordEntityFeedback, type EntityFeedback } from '@ownyou/ikigai';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import { useEngagementLogger } from '../hooks/useEngagementLogger';
import { useMissionRefiner } from '../hooks/useMissionRefiner';
import { PredictionTracker } from '../services/prediction-tracker';
import { recordPredictionOutcome } from '../services/engagement-profile-bridge';
import { useAwardPoints } from '../hooks/useRewards';
import { NS } from '@ownyou/shared-types';
import { MissionEvidencePanel } from '../components/missions/MissionEvidencePanel';
import { CommitSheet } from '../components/missions/CommitSheet';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../lib/mission-spine/instrumentation';
import {
  recordMissionReaction,
  type MissionReaction,
  type MissionReactionRecord,
} from '../lib/missions/reactions';

/* ---------------------------------------------------------------------------
 * Category-specific hero gradients & icons
 * -------------------------------------------------------------------------*/

const CATEGORY_GRADIENTS: Record<string, string> = {
  shopping: 'from-blue-600 to-indigo-800',
  restaurant: 'from-orange-500 to-red-700',
  food: 'from-orange-500 to-red-700',
  travel: 'from-cyan-500 to-blue-700',
  content: 'from-purple-600 to-indigo-800',
  entertainment: 'from-fuchsia-600 to-purple-800',
  events: 'from-fuchsia-600 to-purple-800',
  health: 'from-emerald-500 to-teal-700',
  diagnostic: 'from-emerald-500 to-teal-700',
  savings: 'from-green-500 to-emerald-700',
  people: 'from-pink-500 to-rose-700',
  consumables: 'from-amber-500 to-orange-700',
};

const CATEGORY_ICONS: Record<string, string> = {
  shopping: '\u{1F6CD}\uFE0F',
  restaurant: '\u{1F37D}\uFE0F',
  food: '\u{1F37D}\uFE0F',
  travel: '\u2708\uFE0F',
  content: '\u{1F4DA}',
  entertainment: '\u{1F3AD}',
  events: '\u{1F3AD}',
  health: '\u{1F49A}',
  diagnostic: '\u{1F4CA}',
  savings: '\u{1F4B0}',
  people: '\u{1F465}',
  consumables: '\u{1F6D2}',
};

const URGENCY_COLORS: Record<string, string> = {
  high: 'bg-red-500/20 text-red-400',
  medium: 'bg-amber-500/20 text-amber-400',
  low: 'bg-green-500/20 text-green-400',
};

/* ---------------------------------------------------------------------------
 * MissionDetail route — Store-backed
 * -------------------------------------------------------------------------*/

export function MissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [actionTaken, setActionTaken] = useState<string | null>(null);
  const { mission: storeMission, isLoading } = useMission(id ?? '');
  const { acceptMission, completeMission, dismissMission, snoozeMission } = useUpdateMission();
  const [showCommitSheet, setShowCommitSheet] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const { store, isReady: storeReady } = useStore();
  const { wallet } = useAuth();
  const feedbackUserId = wallet?.address ?? 'anonymous';
  const engagementLogger = useEngagementLogger();
  const { awardBonus } = useAwardPoints();
  const [missionReaction, setMissionReaction] = useState<MissionReaction>('neutral');
  const [reactionNote, setReactionNote] = useState('');

  const predictionTracker = useMemo(() => {
    if (!store || !storeReady) return null;
    return new PredictionTracker(store, feedbackUserId);
  }, [store, storeReady, feedbackUserId]);
  const missionType = (storeMission as unknown as Record<string, unknown>)?.type as string | undefined;
  const { refine, history: refinementHistory, isRefining } = useMissionRefiner(id ?? '', missionType);
  const [refinementInput, setRefinementInput] = useState('');
  const [showRefinement, setShowRefinement] = useState(false);

  useEffect(() => {
    if (!id || !storeMission) return;
    const raw = storeMission as unknown as Record<string, unknown>;
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-02-detail'),
      hop: 'mission_detail_render',
      status: 'success',
      reasonCode: 'mission_detail_ready',
      counts: {
        missionCount: 1,
        evidenceRefCount: Array.isArray(raw.evidenceRefs) ? raw.evidenceRefs.length : 0,
      },
    }, { log: false });
  }, [id, storeMission]);

  useEffect(() => {
    if (!id || !store || !storeReady) return;

    const loadReaction = async () => {
      const stored = await store.get<MissionReactionRecord>(NS.missionFeedback(feedbackUserId), id).catch(() => null);
      if (stored?.reaction) {
        setMissionReaction(stored.reaction);
        setReactionNote(stored.note ?? '');
        return;
      }

      const legacySentiment = (storeMission as unknown as Record<string, unknown> | null)?.feedbackSentiment as 'positive' | 'negative' | undefined;
      if (legacySentiment === 'positive') setMissionReaction('like');
      if (legacySentiment === 'negative') setMissionReaction('dislike');
      setReactionNote('');
    };

    loadReaction();
  }, [feedbackUserId, id, store, storeMission, storeReady]);

  // Map Store mission to display fields, accounting for agent field naming
  const mission = storeMission
    ? (() => {
        const raw = storeMission as unknown as Record<string, unknown>;
        const type = (storeMission.type ?? 'shopping') as string;

        // Extract payload items (content items, deal info, etc.)
        const primaryAction = raw.primaryAction as
          | { label: string; type: string; payload: Record<string, unknown> }
          | undefined;
        const payloadItems = (primaryAction?.payload?.items as Array<{
          title?: string; url?: string; source?: string; type?: string; summary?: string;
          productName?: string; retailer?: string; dealPrice?: number; originalPrice?: number; discountPercent?: number;
        }>) ?? [];
        // Shopping deals stored at top level of mission
        const dealIds = (primaryAction?.payload?.dealIds as string[]) ?? [];

        return {
          id: storeMission.id,
          type,
          title: storeMission.title,
          subtitle:
            storeMission.reason ??
            storeMission.description ??
            (raw.summary as string) ??
            '',
          description:
            storeMission.description ?? (raw.summary as string) ?? '',
          primaryAction: primaryAction as
            | { label: string; type: string; payload: unknown }
            | undefined,
          secondaryActions: raw.secondaryActions as
            | Array<{ label: string; type: string; payload: unknown }>
            | undefined,
          urgency: ((raw.urgency as string) ?? 'medium') as string,
          payloadItems,
          dealIds,
        };
      })()
    : null;

  const handleBack = () => {
    navigate(-1);
  };

  const openPrimaryTarget = () => {
    if (!mission) return;

    const firstItemUrl = mission.payloadItems.find(item => item.url)?.url;
    if (firstItemUrl) {
      window.open(firstItemUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const productName = mission.title.replace(/^(Recommended:\s*|Deal Alert:\s*\d+% off\s*|Shopping:\s*)/i, '').trim();
    if (mission.type === 'shopping' && productName) {
      window.open(`https://www.amazon.com/s?k=${encodeURIComponent(productName)}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (mission.type === 'travel') {
      const destination = mission.title.replace(/^Trip to\s*/i, '').trim();
      if (destination) {
        window.open(`https://www.google.com/travel/flights?q=${encodeURIComponent(destination)}`, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleDoIt = () => {
    setShowCommitSheet(true);
  };

  const handleCommitConfirm = async () => {
    if (!id || !mission) return;
    setIsCommitting(true);
    try {
      await acceptMission(id);
      const raw = storeMission as unknown as Record<string, unknown>;
      engagementLogger?.log({
        missionId: id,
        action: 'clicked_primary',
        agentType: (raw.type as string) ?? 'unknown',
        entitySource: raw.entitySource as string | undefined,
        entityCategory: (raw.entityCategory ?? raw.type) as string | undefined,
        isComposite: !!raw.compositionReasoning,
        isCascade: !!(raw.context as Record<string, unknown>)?.cascadeSource,
        source: ((raw.context as Record<string, unknown>)?.source as 'detection') ?? 'detection',
      }).catch(console.warn);

      if (raw?.entitySource && store) {
        await recordEntityFeedback(feedbackUserId, {
          entityId: raw.entitySource as string,
          runId: (raw.detectionRunId as string) ?? 'unknown',
          type: 'mission_accepted',
          timestamp: Date.now(),
          metadata: { missionId: id },
        }, store as Parameters<typeof recordEntityFeedback>[2]).catch(console.warn);
      }

      openPrimaryTarget();
      setActionTaken('accepted');
      setShowCommitSheet(false);
    } catch (err) {
      console.error('[MissionDetail] Commit failed:', err);
    } finally {
      setIsCommitting(false);
    }
  };

  const handleDone = async () => {
    if (!id || !mission) return;
    try {
      await completeMission(id);
      const raw = storeMission as unknown as Record<string, unknown>;
      if (predictionTracker) {
        recordPredictionOutcome(predictionTracker, { type: (raw.type as string) ?? 'unknown' }, 'completed', (threshold, tokens) => {
          awardBonus('threshold_crossed', `${(raw.type as string) ?? 'Unknown'} accuracy hit ${threshold}%`, tokens, { threshold, category: (raw.type as string) ?? 'unknown' }).catch(() => {});
        }).catch(console.warn);
      }
      setActionTaken('completed');
    } catch (err) {
      console.error('[MissionDetail] Done failed:', err);
    }
  };

  const handleSave = async () => {
    if (id) {
      try {
        // Snoozed engagement event is logged by useMissionLifecycle.bridgeFeedback
        await snoozeMission(id, 168); // 1 week
        setActionTaken('saved');
        setTimeout(() => navigate(-1), 1000);
      } catch (err) {
        console.error('[MissionDetail] Save failed:', err);
      }
    }
  };

  const handleDismiss = async () => {
    if (id) {
      try {
        await dismissMission(id);
        // Sprint 17 Package 5: Feed back to Love Entity confidence model
        const raw = storeMission as unknown as Record<string, unknown>;
        if (raw?.entitySource && store) {
          await recordEntityFeedback(feedbackUserId, {
            entityId: raw.entitySource as string,
            runId: (raw.detectionRunId as string) ?? 'unknown',
            type: 'mission_rejected',
            timestamp: Date.now(),
            metadata: { missionId: id },
          }, store as Parameters<typeof recordEntityFeedback>[2]).catch(console.warn);
        }
        // Record prediction miss for accuracy tracking
        if (predictionTracker) {
          recordPredictionOutcome(predictionTracker, { type: (raw.type as string) ?? 'unknown' }, 'dismissed').catch(console.warn);
        }
        setActionTaken('dismissed');
        navigate(-1);
      } catch (err) {
        console.error('[MissionDetail] Dismiss failed:', err);
      }
    }
  };

  const persistMissionReaction = async (reaction: MissionReaction, note: string) => {
    if (!id || !store || !storeReady) return;

    await recordMissionReaction({
      store,
      userId: feedbackUserId,
      missionId: id,
      reaction,
      source: 'detail',
      note,
    }).catch(console.warn);

    const raw = storeMission as unknown as Record<string, unknown> | null;
    if (raw) {
      const projection = { ...raw };
      if (reaction === 'like') projection.feedbackSentiment = 'positive';
      if (reaction === 'dislike') projection.feedbackSentiment = 'negative';
      if (reaction === 'neutral') delete projection.feedbackSentiment;
      store.put(NS.missionCards(feedbackUserId), id, projection).catch(console.warn);
    }

    if (reaction !== 'neutral' && raw?.entitySource) {
      const feedbackType: EntityFeedback['type'] = reaction === 'like' ? 'tap' : 'dispute';
      recordEntityFeedback(feedbackUserId, {
        entityId: raw.entitySource as string,
        runId: (raw.detectionRunId as string) ?? 'detail-reaction',
        type: feedbackType,
        timestamp: Date.now(),
        metadata: { missionId: id },
      }, store as Parameters<typeof recordEntityFeedback>[2]).catch(console.warn);
    }
  };

  const handleMissionReaction = async (reaction: MissionReaction) => {
    if (!id || !store || !storeReady) return;
    const nextReaction = missionReaction === reaction ? 'neutral' : reaction;
    setMissionReaction(nextReaction);
    await persistMissionReaction(nextReaction, reactionNote);
  };

  const handleReactionNoteSave = async () => {
    await persistMissionReaction(missionReaction, reactionNote);
  };

  if (isLoading) {
    return (
      <div data-testid="mission-detail-screen" className="flex flex-col items-center justify-center min-h-screen bg-[#0F172A] text-slate-100">
        <p className="text-lg font-semibold">Loading mission...</p>
      </div>
    );
  }

  if (!mission) {
    return (
      <div data-testid="mission-detail-screen" className="flex flex-col items-center justify-center min-h-screen bg-[#0F172A] text-slate-100">
        <p className="text-lg font-semibold">Mission not found</p>
        <button onClick={handleBack} className="mt-4 text-sky-400 hover:underline">Go back</button>
      </div>
    );
  }

  const heroGradient = CATEGORY_GRADIENTS[mission.type] ?? 'from-sky-600 to-sky-900';
  const heroIcon = CATEGORY_ICONS[mission.type] ?? '';
  const urgencyColor = URGENCY_COLORS[mission.urgency] ?? URGENCY_COLORS.medium;
  const lifecycleState = (storeMission as unknown as { state?: string } | null)?.state;
  const canComplete = actionTaken === 'accepted' || lifecycleState === 'accepted' || lifecycleState === 'in_progress';
  const isCompleted = actionTaken === 'completed' || lifecycleState === 'completed';
  const canShowActions = !actionTaken && !showCommitSheet && !canComplete && !isCompleted;

  return (
    <div data-testid="mission-detail-screen" className="flex flex-col min-h-screen pb-32 bg-[#0F172A]">
      {/* Hero with category gradient + icon */}
      <div
        data-testid="mission-hero"
        className={cn('h-48 bg-gradient-to-br relative flex items-center justify-center', heroGradient)}
      >
        {heroIcon && (
          <span className="text-5xl opacity-80" aria-hidden="true">{heroIcon}</span>
        )}
        <button
          data-testid="back-button"
          onClick={handleBack}
          className={cn(
            'absolute top-4 left-4 w-10 h-10 rounded-full',
            'bg-slate-900/60 backdrop-blur flex items-center justify-center',
            'text-slate-100 hover:bg-slate-900/80 transition-colors',
          )}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="px-4 -mt-6 relative space-y-4">
        {/* Title & Metadata */}
        <Card className="p-5 bg-slate-800 border-slate-700">
          <div className="flex items-start gap-3 mb-2">
            <h1 data-testid="mission-title" className="text-xl font-bold text-slate-100 flex-1">
              {mission.title}
            </h1>
            <div className="flex gap-2 shrink-0">
              <Badge variant="default">{mission.type}</Badge>
              <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', urgencyColor)}>
                {mission.urgency}
              </span>
            </div>
          </div>
          <p data-testid="mission-subtitle" className="text-sm text-slate-400">
            {mission.subtitle}
          </p>
          {mission.description && mission.description !== mission.subtitle && (
            <p className="text-sm text-slate-300 mt-3">{mission.description}</p>
          )}

        </Card>

        <MissionEvidencePanel mission={storeMission as unknown as Record<string, unknown>} />

        <Card data-testid="mission-reaction-controls" className="p-4 bg-slate-800 border-slate-700">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-100">Reaction</h2>
              <p className="text-xs text-slate-400">Help shape future missions.</p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="mission-reaction-like"
                variant={missionReaction === 'like' ? 'primary' : 'secondary'}
                onClick={() => handleMissionReaction('like')}
              >
                Like
              </Button>
              <Button
                data-testid="mission-reaction-dislike"
                variant={missionReaction === 'dislike' ? 'primary' : 'secondary'}
                onClick={() => handleMissionReaction('dislike')}
              >
                Dislike
              </Button>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <label htmlFor="mission-reaction-note" className="block text-xs font-medium text-slate-300">
              Private note
            </label>
            <textarea
              id="mission-reaction-note"
              data-testid="mission-reaction-note"
              value={reactionNote}
              onChange={(event) => setReactionNote(event.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none"
            />
            <Button
              data-testid="mission-reaction-note-save"
              variant="secondary"
              onClick={handleReactionNoteSave}
            >
              Save note
            </Button>
          </div>
        </Card>

        <Button
          data-testid="open-evidence-route"
          variant="secondary"
          onClick={() => navigate(`/mission/${id}/evidence`)}
          className="w-full md:hidden"
        >
          Open evidence
        </Button>

        {/* Content Items / Deal Items from payload */}
        {mission.payloadItems.length > 0 && (
          <section data-testid="payload-items" className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h2 className="text-lg font-semibold text-slate-100 mb-3">
              {mission.type === 'shopping' ? 'Deals' : mission.type === 'content' ? 'Recommendations' : 'Details'}
            </h2>
            <ul className="space-y-3">
              {mission.payloadItems.map((item, idx) => (
                <li key={idx} className="bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-100 truncate">
                        {item.title || item.productName || 'Item'}
                      </p>
                      {item.summary && (
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{item.summary}</p>
                      )}
                      {item.source && (
                        <p className="text-xs text-slate-500 mt-1">{item.source}</p>
                      )}
                      {item.retailer && (
                        <p className="text-xs text-slate-500 mt-1">{item.retailer}</p>
                      )}
                    </div>
                    {(item.dealPrice != null || item.originalPrice != null) && (
                      <div className="text-right shrink-0">
                        {item.dealPrice != null && (
                          <p className="text-sm font-bold text-emerald-400">${item.dealPrice}</p>
                        )}
                        {item.originalPrice != null && item.originalPrice !== item.dealPrice && (
                          <p className="text-xs text-slate-500 line-through">${item.originalPrice}</p>
                        )}
                        {item.discountPercent != null && item.discountPercent > 0 && (
                          <p className="text-xs text-emerald-400">{item.discountPercent}% off</p>
                        )}
                      </div>
                    )}
                  </div>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-xs text-sky-400 hover:text-sky-300 hover:underline"
                    >
                      Open link
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {showCommitSheet && (
          <CommitSheet
            actionLabel={mission.primaryAction?.label ?? 'Confirm'}
            isSubmitting={isCommitting}
            onCancel={() => setShowCommitSheet(false)}
            onConfirm={handleCommitConfirm}
          />
        )}

        {canComplete && (
          <div data-testid="mission-accepted" className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
            <p className="text-emerald-400 font-medium">Mission accepted</p>
            <p className="text-sm text-slate-400 mt-1">Mark it done when you have completed the local action.</p>
            <Button
              data-testid="done-button"
              variant="primary"
              onClick={handleDone}
              className="mt-3"
            >
              Done
            </Button>
          </div>
        )}

        {isCompleted && (
          <div data-testid="mission-completed" className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
            <p className="text-emerald-400 font-medium">Mission completed</p>
            <button
              onClick={handleBack}
              className="mt-3 text-sm text-sky-400 hover:underline"
            >
              Back to missions
            </button>
          </div>
        )}

        {/* Action Buttons */}
        {canShowActions && (
          <div data-testid="action-buttons" className="flex gap-3">
            <Button
              data-testid="do-it-button"
              variant="primary"
              onClick={handleDoIt}
              className="flex-1"
            >
              {mission.primaryAction?.label ?? 'Do It'}
            </Button>
            <Button
              data-testid="save-button"
              variant="secondary"
              onClick={handleSave}
              className="flex-1"
            >
              Save for Later
            </Button>
            <Button
              data-testid="dismiss-button"
              data-action="dismiss"
              variant="secondary"
              onClick={handleDismiss}
              className="flex-1"
            >
              Not Interested
            </Button>
          </div>
        )}

        {/* Sprint 17g-2: Refinement Chat */}
        <div className="mt-6 border-t border-slate-700 pt-4">
          <Button
            data-testid="refine-toggle"
            variant="secondary"
            onClick={() => setShowRefinement(!showRefinement)}
            className="w-full"
          >
            {showRefinement ? 'Hide Refinement' : 'Refine This Mission'}
          </Button>

          {showRefinement && (
            <div data-testid="refinement-chat" className="mt-4 space-y-3">
              {refinementHistory.map((msg, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm',
                    msg.role === 'user'
                      ? 'bg-indigo-600/20 ml-8'
                      : 'bg-slate-700/50 mr-8',
                  )}
                  {...(msg.role !== 'user' ? { 'data-testid': 'refinement-response' } : {})}
                >
                  {msg.content}
                </div>
              ))}

              {isRefining && (
                <div className="text-sm text-slate-400 animate-pulse">Thinking...</div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid="refinement-input"
                  value={refinementInput}
                  onChange={(e) => setRefinementInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && refinementInput.trim() && !isRefining) {
                      refine(refinementInput.trim());
                      setRefinementInput('');
                    }
                  }}
                  placeholder="e.g., too expensive, closer to home, not in red..."
                  className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-400"
                  disabled={isRefining}
                />
                <Button
                  data-testid="refinement-send"
                  onClick={() => {
                    if (refinementInput.trim() && !isRefining) {
                      refine(refinementInput.trim());
                      setRefinementInput('');
                    }
                  }}
                  disabled={!refinementInput.trim() || isRefining}
                  size="sm"
                >
                  Send
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
